import PropTypes from 'prop-types';
import { clsx } from 'clsx';

/**
 * Service Card component for displaying medical services
 */
const ServiceCard = ({
  title,
  description,
  icon: Icon,
  image,
  onClick,
  className = '',
  featured = false,
}) => {
  return (
    <article
      className={clsx(
        'group relative bg-white rounded-2xl overflow-hidden',
        'border border-neutral-100 transition-all duration-300',
        'hover:shadow-soft-lg hover:-translate-y-1',
        featured && 'ring-2 ring-primary-500',
        className
      )}
    >
      {/* Image Section */}
      {image && (
        <div className="relative h-48 overflow-hidden">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          
          {featured && (
            <span className="absolute top-4 right-4 bg-primary-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
              Featured
            </span>
          )}
        </div>
      )}

      {/* Content Section */}
      <div className="p-6">
        {/* Icon */}
        {Icon && (
          <div className={clsx(
            'w-14 h-14 rounded-xl flex items-center justify-center mb-4',
            'bg-primary-50 text-primary-600 group-hover:bg-primary-100',
            'transition-colors duration-300'
          )}>
            <Icon className="w-7 h-7" aria-hidden="true" />
          </div>
        )}

        {/* Title */}
        <h3 className="text-xl font-semibold text-neutral-900 mb-2 group-hover:text-primary-700 transition-colors">
          {title}
        </h3>

        {/* Description */}
        <p className="text-neutral-600 text-sm leading-relaxed mb-4">
          {description}
        </p>

        {/* CTA Button */}
        {onClick && (
          <button
            onClick={onClick}
            className={clsx(
              'inline-flex items-center text-primary-600 font-medium text-sm',
              'hover:text-primary-700 transition-colors duration-200',
              'group/link'
            )}
          >
            Learn More
            <svg
              className="w-4 h-4 ml-1 transition-transform group-hover/link:translate-x-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
    </article>
  );
};

ServiceCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  image: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
  featured: PropTypes.bool,
};

export default ServiceCard;
